import type { FacilityID, TenantID } from '../../cross-app-shared/types/db.ts';
import type { QuestionnaireDocument, VersionDocument } from '../types/determination-tool.d.ts';

const basePagePatterns = new URLPattern({
  pathname:
    '/:tenantID(\\d+)/:facilityID(\\d+)/:page(home|portal|compliance|tasks|explorer|templates|help|facilities|users|platform)',
});
const determinationPagePatterns = new URLPattern({
  pathname: '/:tenantID(\\d+)/:facilityID(\\d+)/:versionID(Versions_[a-zA-Z0-9_]+)',
});
const editorPagePatterns = new URLPattern({
  pathname: '/:tenantID(\\d+)/:facilityID(\\d+)/:page(editor)/:questionnaireID(Questionnaires_[a-zA-Z0-9_]+)?',
});

export type DeterminationUrlInfoConcrete =
  | {
      page:
        | 'home'
        | 'portal'
        | 'compliance'
        | 'tasks'
        | 'explorer'
        | 'templates'
        | 'help'
        | 'facilities'
        | 'users'
        | 'platform';
      tenantID: TenantID;
      facilityID: FacilityID;
    }
  | { page: 'determination'; tenantID: TenantID; facilityID: FacilityID; versionID: VersionDocument['versionId'] }
  | {
      page: 'editor';
      tenantID: TenantID;
      facilityID: FacilityID;
      questionnaireID: QuestionnaireDocument['questionnaire'] | '';
    };
export type DeterminationUrlInfoOther = { page: 'redirectToHome' } | { page: '404' };
export type DeterminationToolUrlInfo = DeterminationUrlInfoConcrete | DeterminationUrlInfoOther;

export function determinationToolParseUrl(url: string): DeterminationToolUrlInfo {
  if (url === '/') {
    return { page: 'redirectToHome' };
  }

  const baseMatch = basePagePatterns.exec(url, 'http://determinations.localhost:3000');
  if (baseMatch) {
    return {
      page: baseMatch.pathname.groups.page as
        | 'home'
        | 'portal'
        | 'compliance'
        | 'tasks'
        | 'explorer'
        | 'templates'
        | 'help'
        | 'facilities'
        | 'users'
        | 'platform',
      tenantID: Number(baseMatch.pathname.groups.tenantID) as TenantID,
      facilityID: Number(baseMatch.pathname.groups.facilityID) as FacilityID,
    };
  }

  const editorPageMatch = editorPagePatterns.exec(url, 'http://determinations.localhost:3000');
  if (editorPageMatch) {
    return {
      page: 'editor',
      tenantID: Number(editorPageMatch.pathname.groups.tenantID) as TenantID,
      facilityID: Number(editorPageMatch.pathname.groups.facilityID) as FacilityID,
      questionnaireID: (editorPageMatch.pathname.groups.questionnaireID ?? '') as
        | QuestionnaireDocument['questionnaire']
        | '',
    };
  }

  const determinationPageMatch = determinationPagePatterns.exec(url, 'http://determinations.localhost:3000');
  if (determinationPageMatch) {
    return {
      page: 'determination',
      tenantID: Number(determinationPageMatch.pathname.groups.tenantID) as TenantID,
      facilityID: Number(determinationPageMatch.pathname.groups.facilityID) as FacilityID,
      versionID: determinationPageMatch.pathname.groups.versionID as VersionDocument['versionId'],
    };
  }

  return { page: '404' };
}

export type DeterminationToolPageID = ReturnType<typeof determinationToolParseUrl>['page'];

const basePlaceholder = 'http://placeholder.localhost';
export function determinationToolBuildUrl(info: DeterminationToolUrlInfo, base: string | null): string {
  let resultUrl: URL;
  if ('questionnaireID' in info) {
    resultUrl = new URL(`/${info.tenantID}/${info.facilityID}/editor/${info.questionnaireID}`, base ?? basePlaceholder);
  } else if ('versionID' in info) {
    resultUrl = new URL(`/${info.tenantID}/${info.facilityID}/${info.versionID}`, base ?? basePlaceholder);
  } else if ('facilityID' in info) {
    resultUrl = new URL(`/${info.tenantID}/${info.facilityID}/${info.page}`, base ?? basePlaceholder);
  } else {
    resultUrl = new URL('/', base ?? basePlaceholder);
  }

  if (base === null) {
    return resultUrl.toString().replace(basePlaceholder, '');
  } else {
    return resultUrl.toString();
  }
}
